import { useCallback, useEffect, useState } from "react";
import { UserTypes } from "../../../constants/user";
import { InputErrors, InputHints, UserInputsValidLength } from "../../../constants/valids";
import useDebounce from "../../../hooks/lifecycle";
import { RegistrationService } from "../../../services/Registrations";
import { CompanyData, ValidCompanyDataErrors, ValidCompanyDataHints } from "../../../types/user";
import { validateEmail } from "../../../utils/valid";
import { Button } from "../../simples/Button";
import LeftPartUserFields from "./parts/LeftPartUserFields";
import RightPartIndividualUserFields from "./parts/RightPartIndividualUserFields";
import RightPartLegalUserFields from "./parts/RightPartLegalUserFields";
import RightPartSelfEmployedUserFields from "./parts/RightPartSelfEmployedUserFields";
import styles from "./UserFields.module.scss";
import { HintText } from "../../simples/HintText";
import { GENERATE_PASSWORD_HINT } from "../../../constants/hints";

type Props = {
  onSubmit: (data: CompanyData) => Promise<boolean | void>;
  buttonText: string[];
  backError: ValidCompanyDataErrors | undefined;
  initialState: CompanyData;
  fromProfile?: boolean;
};

const commonFields: (keyof CompanyData)[] = [
  "inn",
  "phone",
  "email",
  "form-of-organization",
  "type-of-ownership",
];

const fieldsByOwnership: Record<UserTypes, (keyof CompanyData)[]> = {
  [UserTypes.individual]: [
    ...commonFields,
    "surname",
    "first-name",
    "patronymic",
    "ogrn",
    "okveds",
  ],
  [UserTypes.legal]: [
    ...commonFields,
    "name-organization",
    "full-name",
    "general-director",
    "ogrn",
    "kpp",
    "okveds",
  ],
  [UserTypes.selfEmployed]: [
    ...commonFields,
    "surname",
    "first-name",
    "patronymic",
    "passport-series",
    "passport-number",
    "passport-issue-date",
  ],
};

const UserFields = ({
  onSubmit,
  buttonText,
  backError,
  initialState,
  fromProfile,
}: Props) => {
  const [isBeenChange, setIsBeenChange] = useState(false);
  const [errors, setErrors] = useState<ValidCompanyDataErrors>({});
  const [hints, setHints] = useState<ValidCompanyDataHints>({});
  const [data, setData] = useState(initialState);
  const debouncedInn = useDebounce(data.inn, 1000);

  const setFieldOfData = (field: keyof CompanyData, value: string) => {
    setData((prevState) => ({ ...prevState, [field]: value }));
    !isBeenChange && setIsBeenChange(true);
    setErrors((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setHints((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const submit = async () => {
    if (buttonText.length === 2 && !isBeenChange) {
      setIsBeenChange(true);
      return;
    }

    if (validAllFields()) {
      const isSuccessSubmit = await onSubmit(data);
      isSuccessSubmit && setIsBeenChange(false);
    }
  };

  const validAllFields = () => {
    let isValid = true;
    const nextErrors: ValidCompanyDataErrors = {};
    const fieldsToValidate = fieldsByOwnership[data["type-of-ownership"]];

    fieldsToValidate.forEach((dataKey) => {
      const valueLength = data[dataKey]?.length || 0;
      //is too small
      if (valueLength < 2) {
        nextErrors[dataKey] = [InputErrors.BAD_LEN];
        isValid = false;
      }
      //Is good length
      if (UserInputsValidLength[dataKey]) {
        if (!UserInputsValidLength[dataKey]?.includes(valueLength)) {
          isValid = false;
          nextErrors[dataKey] = [InputErrors.BAD_LEN];
        }
      }
      //Is empty
      if (!valueLength) {
        isValid = false;
        nextErrors[dataKey] = [InputErrors.EMPTY];
      }
    });

    //Is bad email
    if (validateEmail(data.email) === null) {
      isValid = false;
      nextErrors.email = [InputErrors.BAD_EMAIL];
    }

    setErrors(isValid ? {} : nextErrors);

    return isValid;
  };
  const updateData = useCallback(async (inn: string) => {
    const orgData = await RegistrationService.getOrganisationInfoByInn(inn);
    if (orgData) {
      setData((prev) => ({ ...prev, ...orgData }));

      const foundHintByOwnership: Partial<Record<UserTypes, InputHints>> = {
        [UserTypes.individual]: InputHints.FOUND_INDIVIDUAL,
        [UserTypes.legal]: InputHints.FOUND_LEGAL,
        [UserTypes.selfEmployed]: InputHints.FOUND_SELF_EMPLOYED,
      };
      const foundHint = foundHintByOwnership[orgData["type-of-ownership"]];

      setErrors((prev) => {
        if (!prev?.inn) return prev;
        const next = { ...prev };
        delete next.inn;
        return next;
      });

      setHints((prev) =>
        foundHint
          ? { ...prev, inn: [foundHint] }
          : prev
      );
    } else {
      setHints((prev) =>
        prev
          ? { ...prev, inn: [InputHints.NOT_FOUND_INN] }
          : { inn: [InputHints.NOT_FOUND_INN] }
      );
    }
  }, []);

  //on type inn field debaunced
  useEffect(() => {
    const isLegalInn = data.inn.length === 10;
    const isIndividualInn = data.inn.length === 12;
    const isEmptyProfile = !!data["form-of-organization"];
    const isGoodInn = isIndividualInn || isLegalInn;
    const isChangedFromProfile = !fromProfile || isBeenChange;
    if (isGoodInn && debouncedInn && isChangedFromProfile) {
      (isEmptyProfile || !fromProfile) && updateData(data.inn);
    }
  }, [debouncedInn]);

  // set initial data;
  useEffect(() => {
    fromProfile && setData(initialState);
    !fromProfile && !data.inn && setData(initialState);
    !fromProfile && setIsBeenChange(true);
  }, [initialState]);

  //set errors from back
  useEffect(() => {
    backError &&
      setErrors((prev) => (prev ? { ...prev, ...backError } : backError));
  }, [backError]);
  const ownershipType = data["type-of-ownership"];
  const rightPartByOwnership = {
      [UserTypes.legal]: RightPartLegalUserFields,
      [UserTypes.individual]: RightPartIndividualUserFields,
      [UserTypes.selfEmployed]: RightPartSelfEmployedUserFields,
  };
  const RightPartFields = rightPartByOwnership[ownershipType] || RightPartSelfEmployedUserFields;

  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <LeftPartUserFields
          errors={errors}
          hints={hints}
          setData={setFieldOfData}
          data={data}
          isBeenChange={isBeenChange}
        />
        <RightPartFields
          errors={errors}
          data={data}
          setData={setFieldOfData}
          isBeenChange={isBeenChange}
        />
      </div>
      <div className={styles.button}>
        <span className={styles.hintMobile}>
          <HintText mt={24}>{GENERATE_PASSWORD_HINT}</HintText>
        </span>
        <Button onClick={submit} disabled={!!Object.keys(errors).length}>
          {buttonText && isBeenChange && buttonText[1]
            ? buttonText[1]
            : buttonText[0]}
        </Button>
      </div>
    </div>
  );
};
export default UserFields;
